import React, { useState, useContext } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import toast from 'react-hot-toast';
import { useTranslation } from '../../hooks/useTranslation';
import { GlobalContext } from '../../contexts/GlobalContext';
import Icon from 'react-icons-kit';
import { checkCircle, paperPlane } from 'react-icons-kit/fa';
import InfoPage from '../info/InfoPage';
import { InfoProse } from '../info/InfoContent';
import Input from '../ui/Input';
import Label from '../ui/Label';
import Textarea from '../ui/Textarea';
import CommerceButton from '../ui/CommerceButton';
import Api from '../../utils/api';

export default function WithdrawalForm() {
  const { t, locale } = useTranslation('withdrawal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const globalContext = useContext(GlobalContext);
  const currentUser = globalContext?.user;

  const withdrawalSchema = z.object({
    name: z.string().min(2, t('form.validation.nameRequired')),
    orderNumber: z.string().min(2, t('form.validation.orderNumberRequired')),
    email: z.string().email(t('form.validation.emailInvalid')),
    details: z.string().optional()
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(withdrawalSchema),
    defaultValues: {
      name: currentUser?.name || '',
      orderNumber: '',
      email: currentUser?.email || '',
      details: ''
    }
  });

  const formatDateTime = (iso) =>
    new Intl.DateTimeFormat(locale === 'ro' ? 'ro-RO' : 'en-GB', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: 'Europe/Bucharest'
    }).format(new Date(iso));

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await Api.post('/v2/order-withdrawals', {}, {}, {
        name: data.name,
        orderNumber: data.orderNumber,
        email: data.email,
        details: data.details || null
      });

      if (response.success && response.data) {
        setReceipt({ receivedAt: response.data.receivedAt, email: data.email });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(response.message || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error(t('form.errorGeneric'));
      setIsSubmitting(false);
    }
  };

  return (
    <InfoPage title={t('form.title')}>
      <InfoProse>
        <p>
          {t('form.intro')}
        </p>
        <p>
          {t('form.policyPrefix')}{' '}
          <Link href="/info/politica-retur" className="text-teal-600 underline hover:text-teal-700">
            {t('form.policyLink')}
          </Link>.
        </p>
      </InfoProse>

      {receipt ? (
        <div role="status" className="rounded-xl bg-krit-paper p-5 sm:p-8">
          <div className="mb-4 flex items-start gap-3">
            <span aria-hidden="true" className="text-teal-700"><Icon icon={checkCircle} size={28} /></span>
            <h2 className="text-xl font-black leading-snug">
              {t('form.successTitle')}
            </h2>
          </div>
          <p className="mb-2 text-sm leading-relaxed">
            {t('form.successReceived').replace('{datetime}', formatDateTime(receipt.receivedAt))}
          </p>
          <p className="break-words text-sm leading-relaxed">
            {t('form.successEmail').replace('{email}', receipt.email)}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} aria-busy={isSubmitting} className="space-y-5 rounded-xl bg-krit-paper p-5 sm:p-8">
          <div>
            <Label htmlFor="withdrawal-name" className="mb-2 block !text-[12px] !font-bold !normal-case !leading-relaxed !text-krit-surface">
              {t('form.name')} *
            </Label>
            <Input
              {...register('name')}
              id="withdrawal-name"
              type="text"
              placeholder={t('form.namePlaceholder')}
              disabled={isSubmitting}
              autoComplete="name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'withdrawal-name-error' : undefined}
              className="!h-[48px] !rounded-lg !border-0 !px-4 !text-[16px] !text-krit-surface"
            />
            {errors.name && (
              <p id="withdrawal-name-error" role="alert" className="mt-2 text-[12px] text-red-700">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="withdrawal-order-number" className="mb-2 block !text-[12px] !font-bold !normal-case !leading-relaxed !text-krit-surface">
              {t('form.orderNumber')} *
            </Label>
            <Input
              {...register('orderNumber')}
              id="withdrawal-order-number"
              type="text"
              placeholder={t('form.orderNumberPlaceholder')}
              disabled={isSubmitting}
              aria-invalid={!!errors.orderNumber}
              aria-describedby={`withdrawal-order-number-hint${errors.orderNumber ? ' withdrawal-order-number-error' : ''}`}
              className="!h-[48px] !rounded-lg !border-0 !px-4 !text-[16px] !text-krit-surface"
            />
            <p id="withdrawal-order-number-hint" className="mt-2 text-[12px] leading-relaxed text-krit-muted">{t('form.orderNumberHint')}</p>
            {errors.orderNumber && (
              <p id="withdrawal-order-number-error" role="alert" className="mt-2 text-[12px] text-red-700">{errors.orderNumber.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="withdrawal-email" className="mb-2 block !text-[12px] !font-bold !normal-case !leading-relaxed !text-krit-surface">
              {t('form.email')} *
            </Label>
            <Input
              {...register('email')}
              id="withdrawal-email"
              type="email"
              placeholder={t('form.emailPlaceholder')}
              disabled={isSubmitting}
              autoComplete="email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'withdrawal-email-error' : undefined}
              className="!h-[48px] !rounded-lg !border-0 !px-4 !text-[16px] !text-krit-surface"
            />
            {errors.email && (
              <p id="withdrawal-email-error" role="alert" className="mt-2 text-[12px] text-red-700">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="withdrawal-details" className="mb-2 block !text-[12px] !font-bold !normal-case !leading-relaxed !text-krit-surface">
              {t('form.details')}
            </Label>
            <Textarea
              {...register('details')}
              id="withdrawal-details"
              rows={4}
              placeholder={t('form.detailsPlaceholder')}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <CommerceButton type="submit" availability="supplier" icon={paperPlane} subtext={null} loading={isSubmitting}
              text={t(isSubmitting ? 'form.submitting' : 'form.confirm')} className="!min-h-[48px] !max-w-[360px]" />
            <p className="mt-3 text-[12px] leading-relaxed text-krit-muted">{t('form.confirmNote')}</p>
          </div>
        </form>
      )}
    </InfoPage>
  );
}
